import { NormalizedFormState } from '../../../config.models';
import { DeviceTypeCode, SensorNames } from 'app/shared/enums/device-type-code';
import { Mp1SensorIds, Mp2SensorIds, TritonInfoFieldId, TritonSectionIds, TritonSensors, getSensorFieldId } from '../base-config-models';
import {
    AvGatedSensorFieldIds,
    LongRangeSensorFieldIds,
    PeakComboSensorFieldIds,
    SamplerFieldIds,
    SmartDepthSensorFieldIds,
    SurfaceComboSensorFieldIds,
} from './config-models';
import { SelectField } from '../../../models/field-models';

export function getSensorDataByName(name: string, state: NormalizedFormState, mp: 1 | 2) {
    if (!name) {
        return null;
    }

    switch (name) {
        case SensorNames.avGated1:
        case SensorNames.avGated2:
            return gatherAvGatedSensor(state, mp);
        case SensorNames.lrd1:
        case SensorNames.lrd2:
            return gatherLongRangeSensor(state, mp);
        case SensorNames.peakCombo1:
        case SensorNames.peakCombo2:
            return gatherPeakComboSensor(state, mp);
        case SensorNames.smart1:
        case SensorNames.smart2:
            return gatherSmartDepthSensor(state, mp);
        case SensorNames.surface1:
        case SensorNames.surface2:
            return gatherSurfaceComboSensor(state, mp);
        default:
            console.warn(`Sensor ${name} not found`);
            return null;
    }
}

export function gatherAvGatedSensor(state: NormalizedFormState, mp: 1 | 2) {
    const tritonInfoSection = state.config.sections.find((v) => v.id === TritonSectionIds.TritonInfo);

    const sensorIndex = (mp - 1) as 0 | 1;
    const mpField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.MP1 : TritonInfoFieldId.MP2] as SelectField<unknown, number>;
    const channelField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.Channel1 : TritonInfoFieldId.Channel2] as SelectField<unknown, number>;
    const monitoringPoint = mpField.value as 0 | 1;

    const sensorSection = state.config.sections.find(
        (v) =>
            v.id ===
            (sensorIndex === 0 ? Mp1SensorIds[TritonSensors.AvGated] : Mp2SensorIds[TritonSensors.AvGated]),
    );

    const result: { [key: string]: unknown } = {
        name: monitoringPoint === 0 ? SensorNames.avGated1 : SensorNames.avGated2,
        ultrasonicFlowType: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.UltrasonicFlowType, sensorIndex)].value,
        physicalOffset: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.PhysicalOffset, sensorIndex)].value,
        ultrasonicElectronicOffset: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.UltrasonicElectronicOffset, sensorIndex)].value,
        ultrasonicDeadband: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.UltrasonicDeadband, sensorIndex)].value,
        ultrasonicStoreData: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.UltrasonicStoreData, sensorIndex)].value,
        ultrasonicEnable: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.UltrasonicEnable, sensorIndex)].value,
        pressureEnable: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.PressureEnable, sensorIndex)].value,
        pressureElectronicOffset: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.PressureElectronicOffset, sensorIndex)].value,
        pressureStoreData: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.PressureStoreData, sensorIndex)].value,
        pressureCalibration: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.PressureCalibration, sensorIndex)].value,
        velocityEnable: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityEnable, sensorIndex)].value,
        velocityDirection: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityDirection, sensorIndex)].value,
        velocityMinimum: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityMinimum, sensorIndex)].value,
        velocityMaximumAVGatedValue: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityMaximum, sensorIndex)].value,
        velocityMaximum: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityMaximum, sensorIndex)].value,
        velocityAcquisition: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityAcquisition, sensorIndex)].value,
        velocityStoreData: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityStoreData, sensorIndex)].value,
        velocityScrubbing: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityScrubbing, sensorIndex)].value,
        temperatureStoreData: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.TemperatureStoreData, sensorIndex)].value,
        velocityGain: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityGain, sensorIndex)].value,
        velocitySensitivity: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocitySensitivity, sensorIndex)].value,
        velocityDacGain: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityDacGain, sensorIndex)].value,
        velocityPingAmplitude: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityPingAmplitude, sensorIndex)].value,
        velocityFrequency: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.VelocityFrequency, sensorIndex)].value,
        ultrasonicDacGain: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.UltrasonicDacGain, sensorIndex)].value,
        ultrasonicPingAmplitude: sensorSection.fields[getSensorFieldId(AvGatedSensorFieldIds.UltrasonicPingAmplitude, sensorIndex)].value,
        installationNumber: monitoringPoint,
        channel: channelField.value,
        type: monitoringPoint === 0 ? DeviceTypeCode.AVGated1 : DeviceTypeCode.AVGated2,
    };

    return result;
}

export function gatherLongRangeSensor(state: NormalizedFormState, mp: 1 | 2) {
    const tritonInfoSection = state.config.sections.find((v) => v.id === TritonSectionIds.TritonInfo);

    const sensorIndex = (mp - 1) as 0 | 1;
    const mpField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.MP1 : TritonInfoFieldId.MP2] as SelectField<unknown, number>;
    const channelField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.Channel1 : TritonInfoFieldId.Channel2] as SelectField<unknown, number>;
    const monitoringPoint = mpField.value as 0 | 1;

    const sensorSection = state.config.sections.find(
        (v) =>
            v.id ===
            (sensorIndex === 0 ? Mp1SensorIds[TritonSensors.LongRange] : Mp2SensorIds[TritonSensors.LongRange]),
    );

    const result: { [key: string]: unknown } = {
        name: monitoringPoint === 0 ? SensorNames.lrd1 : SensorNames.lrd2,
        physicalOffset: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.PhysicalOffset, sensorIndex)].value,
        storeData: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.StoreData, sensorIndex)].value,
        blanking: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.Blanking, sensorIndex)].value,
        temperatureSensor: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.TemperatureSensor, sensorIndex)].value,
        longRangeDepthTritonSensorPowerModel: 'Auto',
        power: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.Power, sensorIndex)].value,
        electronicOffset: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.ElectronicOffset, sensorIndex)].value,
        gainControlSelection: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.GainControlSelection, sensorIndex)].value,
        gainControl: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.GainControl, sensorIndex)].value,
        compensatedTemperatureMarch: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.CompensatedTemperatureMarch, sensorIndex)].value,
        compensatedTemperatureSeptember:
            sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.CompensatedTemperatureSeptember, sensorIndex)].value,
        disableCapacitance: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.DisableCapacitance, sensorIndex)].value,
        logCapacitance: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.LogCapacitance, sensorIndex)].value,
        surchargeThreshold: sensorSection.fields[getSensorFieldId(LongRangeSensorFieldIds.SurchargeThreshold, sensorIndex)].value,
        installationNumber: monitoringPoint,
        channel: channelField.value,
        type: monitoringPoint === 0 ? DeviceTypeCode.LongRangeDepth1 : DeviceTypeCode.LongRangeDepth2,
    };

    return result;
}

export function gatherPeakComboSensor(state: NormalizedFormState, mp: 1 | 2) {
    const tritonInfoSection = state.config.sections.find((v) => v.id === TritonSectionIds.TritonInfo);

    const sensorIndex = (mp - 1) as 0 | 1;
    const mpField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.MP1 : TritonInfoFieldId.MP2] as SelectField<unknown, number>;
    const channelField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.Channel1 : TritonInfoFieldId.Channel2] as SelectField<unknown, number>;
    const monitoringPoint = mpField.value as 0 | 1;

    const sensorSection = state.config.sections.find(
        (v) =>
            v.id ===
            (sensorIndex === 0
                ? Mp1SensorIds[TritonSensors.PeakCombo]
                : Mp2SensorIds[TritonSensors.PeakCombo]),
    );

    const result: { [key: string]: unknown } = {
        name: monitoringPoint === 0 ? SensorNames.peakCombo1 : SensorNames.peakCombo2,
        ultrasonicFlowType: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.UltrasonicFlowType, sensorIndex)].value,
        physicalOffset: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.PhysicalOffset, sensorIndex)].value,
        ultrasonicEnable: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.UltrasonicEnable, sensorIndex)].value,
        ultrasonicElectronicOffset: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.UltrasonicElectronicOffset, sensorIndex)].value,
        ultrasonicDeadband: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.UltrasonicDeadband, sensorIndex)].value,
        ultrasonicStoreData: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.UltrasonicStoreData, sensorIndex)].value,
        pressureEnable: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.PressureEnable, sensorIndex)].value,
        pressureElectronicOffset: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.PressureElectronicOffset, sensorIndex)].value,
        pressureCalibration: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.PressureCalibration, sensorIndex)].value,
        pressureStoreData: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.PressureStoreData, sensorIndex)].value,
        velocityEnable: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.VelocityEnable, sensorIndex)].value,
        velocitySensitivity: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.VelocitySensitivity, sensorIndex)].value,
        velocityDirection: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.VelocityDirection, sensorIndex)].value,
        velocityGain: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.VelocityGain, sensorIndex)].value,
        velocityMaximum: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.VelocityMaximum, sensorIndex)].value,
        velocityScrubbing: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.VelocityScrubbing, sensorIndex)].value,
        velocityStoreData: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.VelocityStoreData, sensorIndex)].value,
        temperatureStoreData: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.TemperatureStoreData, sensorIndex)].value,
        velocityFlowType: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.VelocityFlowType, sensorIndex)].value,
        flowType: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.UltrasonicFlowType, sensorIndex)].value,
        transmitFrequency: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.TransmitFrequency, sensorIndex)].value,
        sensor: sensorSection.fields[getSensorFieldId(PeakComboSensorFieldIds.Sensor, sensorIndex)].value,
        installationNumber: monitoringPoint,
        channel: channelField.value,
        type: monitoringPoint === 0 ? DeviceTypeCode.PeakCombo1 : DeviceTypeCode.PeakCombo2,
    };

    return result;
}

export function gatherSmartDepthSensor(state: NormalizedFormState, mp: 1 | 2) {
    const tritonInfoSection = state.config.sections.find((v) => v.id === TritonSectionIds.TritonInfo);

    const sensorIndex = (mp - 1) as 0 | 1;
    const mpField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.MP1 : TritonInfoFieldId.MP2] as SelectField<unknown, number>;
    const channelField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.Channel1 : TritonInfoFieldId.Channel2] as SelectField<unknown, number>;
    const monitoringPoint = mpField.value as 0 | 1;

    const sensorSection = state.config.sections.find(
        (v) =>
            v.id ===
            (sensorIndex === 0 ? Mp1SensorIds[TritonSensors.SmartDepth] : Mp2SensorIds[TritonSensors.SmartDepth]),
    );

    const result: { [key: string]: unknown } = {
        name: monitoringPoint === 0 ? SensorNames.smart1 : SensorNames.smart2,
        temperatureSensor: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.TemperatureSensor, sensorIndex)].value,
        physicalOffset: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.PhysicalOffset, sensorIndex)].value,
        ultrasonicElectronicOffset: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicElectronicOffset, sensorIndex)].value,
        ultrasonicMode: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicMode, sensorIndex)].value,
        ultrasonicBlanking: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicBlanking, sensorIndex)].value,
        ultrasonicStoreData: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicStoreData, sensorIndex)].value,
        ultrasonicDisableSurchargeDetection:
            sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicDisableSurchargeDetection, sensorIndex)].value,
        temperatureStoreData: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.TemperatureStoreData, sensorIndex)].value,
        ultrasonicDacGain: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicDacGain, sensorIndex)].value,
        sensor: sensorSection.fields[getSensorFieldId(SmartDepthSensorFieldIds.TemperatureSensor, sensorIndex)].value,
        installationNumber: monitoringPoint,
        channel: channelField.value,
        type: monitoringPoint === 0 ? DeviceTypeCode.TritonSmartBat1 : DeviceTypeCode.TritonSmartBat2,
    };

    return result;
}

export function gatherSurfaceComboSensor(state: NormalizedFormState, mp: 1 | 2) {
    const tritonInfoSection = state.config.sections.find((v) => v.id === TritonSectionIds.TritonInfo);

    const sensorIndex = (mp - 1) as 0 | 1;
    const mpField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.MP1 : TritonInfoFieldId.MP2] as SelectField<unknown, number>;
    const channelField = tritonInfoSection.fields[mp === 1 ? TritonInfoFieldId.Channel1 : TritonInfoFieldId.Channel2] as SelectField<unknown, number>;
    const monitoringPoint = mpField.value as 0 | 1;

    const sensorSection = state.config.sections.find(
        (v) =>
            v.id ===
            (sensorIndex === 0
                ? Mp1SensorIds[TritonSensors.SurfaceCombo]
                : Mp2SensorIds[TritonSensors.SurfaceCombo]),
    );

    const result: { [key: string]: unknown } = {
        name: monitoringPoint === 0 ? SensorNames.surface1 : SensorNames.surface2,
        physicalOffset: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.PhysicalOffset, sensorIndex)].value,
        ultrasonicElectronicOffset: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicElectronicOffset, sensorIndex)].value,
        ultrasonicMode: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicMode, sensorIndex)].value,
        ultrasonicBlanking: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicBlanking, sensorIndex)].value,
        ultrasonicDisableSurchargeDetection:
            sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicDisableSurchargeDetection, sensorIndex)].value,
        ultrasonicStoreData: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicStoreData, sensorIndex)].value,
        surfaceVelocityEnable: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityEnable, sensorIndex)].value,
        surfaceVelocityFrequency: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityFrequency, sensorIndex)].value,
        surfaceVelocitySensitivity: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocitySensitivity, sensorIndex)].value,
        surfaceVelocityDirection: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityDirection, sensorIndex)].value,
        surfaceVelocityMaximum: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityMaximum, sensorIndex)].value,
        surfaceVelocityGain: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityGain, sensorIndex)].value,
        surfaceVelocityDuration: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityDuration, sensorIndex)].value,
        surfaceVelocityScrubbing: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityScrubbing, sensorIndex)].value,
        surfaceVelocityStoreData: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityStoreData, sensorIndex)].value,
        velocityEnable: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.VelocityEnable, sensorIndex)].value,
        velocityFrequency: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.VelocityFrequency, sensorIndex)].value,
        velocitySensitivity: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.VelocitySensitivity, sensorIndex)].value,
        velocityDirection: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.VelocityDirection, sensorIndex)].value,
        velocityMaximum: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.VelocityMaximum, sensorIndex)].value,
        velocityGain: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.VelocityGain, sensorIndex)].value,
        velocityStoreData: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.VelocityStoreData, sensorIndex)].value,
        temperatureStoreData: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.TemperatureStoreData, sensorIndex)].value,
        pressureEnable: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.PressureEnable, sensorIndex)].value,
        pressureElectronicOffset: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.PressureElectronicOffset, sensorIndex)].value,
        pressureCalibration: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.PressureCalibration, sensorIndex)].value,
        pressureStoreData: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.PressureStoreData, sensorIndex)].value,
        velocityScrubbing: sensorSection.fields[getSensorFieldId(SurfaceComboSensorFieldIds.VelocityScrubbing, sensorIndex)].value,
        sensor: 1,
        installationNumber: monitoringPoint,
        channel: channelField.value,
        type: monitoringPoint === 0 ? DeviceTypeCode.SurfaceCombo1 : DeviceTypeCode.SurfaceCombo2,
    };

    return result;
}

export function gatherSamplerInfo(state: NormalizedFormState) {
    const samplerInfo = state.config.sections.find((v) => v.id === TritonSectionIds.Sampler);

    if (!samplerInfo) {
        return null;
    }

    const result: { [key: string]: unknown } = {
        name: 'Sampler',
        active: true,
        delay: samplerInfo.fields[SamplerFieldIds.Delay].value,
        samplesPerWakeup: samplerInfo.fields[SamplerFieldIds.SamplesPerWakeup].value,
        samplingType: samplerInfo.fields[SamplerFieldIds.SamplingType].value,
        trigger: samplerInfo.fields[SamplerFieldIds.Trigger].value,
        installationNumber: 0,
        type: DeviceTypeCode.Sampler,
    }

    return result;
}
